import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchCreatorPartnerUsernames,
  fetchSocialUsernames,
  validateSocialHandle,
  fetchCompetitors,
} from "../thunks/brandSocialHandlesThunk.tsx";
import {
  CompetitorItemType,
  CompetitorLocalItemType,
  PlatformsUsernameState,
  UsernameItem,
  UsernameItemType,
  ValidateDataType,
} from "../types/brandSocialHandlesTypes.tsx";

interface State {
  isLoadingSocialUsernames: boolean;
  isLoadingCompetitors: boolean;
  isLoadingCreatorPartnerUsernames: boolean;
  isLoadingValidate: boolean;
  validSocialUsernames: ValidateDataType[];
  usernames: PlatformsUsernameState;
  usernamesOrder: number[];
  competitors: CompetitorLocalItemType[];
  partnerUsernames: PlatformsUsernameState;
  partnerUsernamesOrder: number[];
  usernamesToDelete: number[];
  competitorsToDelete: number[];
  partnerUsernamesToDelete: number[];
}

const initialState: State = {
  isLoadingSocialUsernames: false,
  isLoadingCompetitors: false,
  isLoadingCreatorPartnerUsernames: false,
  isLoadingValidate: false,
  validSocialUsernames: [],
  usernames: {},
  usernamesOrder: [],
  partnerUsernamesOrder: [],
  competitors: [],
  partnerUsernames: {},
  usernamesToDelete: [],
  competitorsToDelete: [],
  partnerUsernamesToDelete: [],
};

const getUsernamesByPlatform = (data: UsernameItemType[]) => {
  const result: PlatformsUsernameState = {};

  data.forEach(({ id, platform, username }) => {
    const platformId = platform.id;
    if (!result[platformId]) {
      result[platformId] = [];
    }
    result[platformId].push({
      id,
      username,
      status: "valid",
      mode: "existing",
    });
  });
  return result;
};

const selectPlatform = ({
  prev,
  platformId,
  oldPlatformId,
  orders,
}: {
  prev: PlatformsUsernameState;
  oldPlatformId?: number;
  platformId: number;
  orders: number[];
}) => {
  const next = { ...prev };
  if (oldPlatformId !== undefined) {
    delete next[oldPlatformId];
  }
  if (!next[platformId]) {
    next[platformId] = [
      {
        id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
        username: "",
        status: "none",
        mode: "new",
      },
    ];
  }

  const ordersResult = oldPlatformId
    ? [...orders.filter((item) => item !== oldPlatformId), platformId]
    : [...orders, platformId];
  return { next, ordersResult };
};

const removePlatform = ({
  prev,
  platformId,
  orders,
}: {
  prev: PlatformsUsernameState;
  platformId: number;
  orders: number[];
}) => {
  const removedItems: UsernameItem[] = prev[platformId] ?? [];
  const { [platformId]: _, ...rest } = prev;
  const deleteList = removedItems
    .filter((item) => item.mode === "existing" || item.mode === "edited")
    .map(({ id }) => id);

  const ordersResult = orders.filter((item) => item !== platformId);

  return { rest, deleteList, ordersResult };
};
const addNewUsernameSection = ({
  prev,
  platformId,
}: {
  prev: PlatformsUsernameState;
  platformId: number;
}) => {
  const newElem: UsernameItem = {
    id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
    username: "",
    status: "none",
    mode: "new",
  };
  return {
    ...prev,
    [platformId]: [...(prev[platformId] ?? []), newElem],
  };
};

const removeUsernameItemSection = ({
  prev,
  platformId,
  usernameId,
}: {
  prev: PlatformsUsernameState;
  platformId: number;
  usernameId: number;
}) => {
  const current = prev[platformId] ?? [];
  const removedItem = current.find((item) => item.id === usernameId);
  const updated: UsernameItem[] = current.filter(
    (item) => item.id !== usernameId,
  );

  const result: { rest: PlatformsUsernameState; removedItem: number | null } = {
    rest: { ...prev, [platformId]: updated },
    removedItem:
      removedItem?.mode === "existing" || removedItem?.mode === "edited"
        ? removedItem.id
        : null,
  };

  return result;
};

const updateUsernameItem = ({
  prev,
  platformId,
  data,
}: {
  prev: PlatformsUsernameState;
  platformId: number;
  data: UsernameItem;
}) => {
  const rest = {
    ...prev,
    [platformId]: (prev[platformId] ?? []).map((item) =>
      item.id === data.id ? data : item,
    ),
  };
  const removedItem =
    !data.username && (data.mode === "existing" || data.mode === "edited")
      ? data.id
      : null;
  return { rest, removedItem };
};

const getNewCompetitorItem: () => CompetitorLocalItemType = () => ({
  id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
  website: "",
  strength: "strong",
  mode: "new",
  social_usernames: [],
  orders: [],
});

export const brandSocialHandlesSlice = createSlice({
  name: "brandSocialHandles",
  initialState,
  reducers: {
    selectPlatformUsername: (
      state,
      action: PayloadAction<{ platformId: number; oldPlatformId?: number }>,
    ) => {
      const result = selectPlatform({
        ...action.payload,
        prev: state.usernames,
        orders: state.usernamesOrder,
      });
      state.usernames = result.next;
      state.usernamesOrder = result.ordersResult;
    },
    selectPlatformPartnerUsername: (
      state,
      action: PayloadAction<{ platformId: number; oldPlatformId?: number }>,
    ) => {
      const result = selectPlatform({
        ...action.payload,
        prev: state.partnerUsernames,
        orders: state.partnerUsernamesOrder,
      });
      state.partnerUsernames = result.next;
      state.partnerUsernamesOrder = result.ordersResult;
    },
    removePlatformWithUsernames: (
      state,
      action: PayloadAction<{ platformId: number }>,
    ) => {
      if (action.payload) {
        const result = removePlatform({
          ...action.payload,
          prev: state.usernames,
          orders: state.usernamesOrder,
        });
        state.usernames = result.rest;
        state.usernamesToDelete = [
          ...state.usernamesToDelete,
          ...result.deleteList,
        ];
        state.usernamesOrder = result.ordersResult;
      }
    },
    removePlatformWithPartnerUsernames: (
      state,
      action: PayloadAction<{ platformId: number }>,
    ) => {
      if (action.payload) {
        const result = removePlatform({
          ...action.payload,
          prev: state.partnerUsernames,
          orders: state.partnerUsernamesOrder,
        });
        state.partnerUsernames = result.rest;
        state.partnerUsernamesToDelete = [
          ...state.partnerUsernamesToDelete,
          ...result.deleteList,
        ];
        state.partnerUsernamesOrder = result.ordersResult;
      }
    },
    addUsernameSection: (
      state,
      action: PayloadAction<{ platformId: number }>,
    ) => {
      if (action.payload) {
        state.usernames = addNewUsernameSection({
          ...action.payload,
          prev: state.usernames,
        });
      }
    },
    addPartnerUsernameSection: (
      state,
      action: PayloadAction<{ platformId: number }>,
    ) => {
      if (action.payload) {
        state.partnerUsernames = addNewUsernameSection({
          ...action.payload,
          prev: state.partnerUsernames,
        });
      }
    },
    removeUsernameSection: (
      state,
      action: PayloadAction<{ platformId: number; usernameId: number }>,
    ) => {
      if (action.payload) {
        const result = removeUsernameItemSection({
          ...action.payload,
          prev: state.usernames,
        });
        state.usernames = result.rest;
        if (result.removedItem) {
          state.usernamesToDelete = [
            ...state.usernamesToDelete,
            result.removedItem,
          ];
        }
      }
    },
    removePartnerUsernameSection: (
      state,
      action: PayloadAction<{ platformId: number; usernameId: number }>,
    ) => {
      if (action.payload) {
        const result = removeUsernameItemSection({
          ...action.payload,
          prev: state.partnerUsernames,
        });
        state.partnerUsernames = result.rest;
        if (result.removedItem) {
          state.partnerUsernamesToDelete = [
            ...state.partnerUsernamesToDelete,
            result.removedItem,
          ];
        }
      }
    },
    updateUsernameSection: (
      state,
      action: PayloadAction<{
        platformId: number;
        data: UsernameItem;
      }>,
    ) => {
      if (action.payload) {
        const result = updateUsernameItem({
          ...action.payload,
          prev: state.usernames,
        });
        state.usernames = result.rest;
        if (result.removedItem) {
          state.usernamesToDelete = [
            ...state.usernamesToDelete,
            result.removedItem,
          ];
        }
      }
    },
    updatePartnerUsernameSection: (
      state,
      action: PayloadAction<{
        platformId: number;
        data: UsernameItem;
      }>,
    ) => {
      if (action.payload) {
        const result = updateUsernameItem({
          ...action.payload,
          prev: state.partnerUsernames,
        });
        state.partnerUsernames = result.rest;
        if (result.removedItem) {
          state.partnerUsernamesToDelete = [
            ...state.partnerUsernamesToDelete,
            result.removedItem,
          ];
        }
      }
    },
    addCompetitor: (state) => {
      const newCompetitor: CompetitorLocalItemType = getNewCompetitorItem();
      state.competitors = [...state.competitors, newCompetitor];
    },
    removeCompetitor: (state, action: PayloadAction<number>) => {
      const competitorId = action.payload;
      const removedItem: CompetitorLocalItemType | undefined =
        state.competitors.find((item) => item.id === competitorId);

      state.competitors = state.competitors.filter(
        (item) => item.id !== competitorId,
      );
      if (removedItem?.mode === "existing" || removedItem?.mode === "edited") {
        state.competitorsToDelete = [
          ...state.competitorsToDelete,
          competitorId,
        ];
      }
    },
    updateCompetitor: (
      state,
      action: PayloadAction<CompetitorLocalItemType>,
    ) => {
      state.competitors = state.competitors.map((item) =>
        item.id === action.payload.id ? action.payload : item,
      );
    },
    selectCompetitorPlatform: (
      state,
      action: PayloadAction<{
        competitorId: number;
        platformId: number;
        oldPlatformId?: number;
      }>,
    ) => {
      state.competitors = state.competitors.map((item) => {
        if (item.id === action.payload.competitorId) {
          const result = selectPlatform({
            prev: item.social_usernames,
            platformId: action.payload.platformId,
            oldPlatformId: action.payload.oldPlatformId,
            orders: item.orders,
          });
          return {
            ...item,
            social_usernames: result.next,
            orders: result.ordersResult,
          };
        } else {
          return item;
        }
      });
    },
    removeCompetitorPlatform: (
      state,
      action: PayloadAction<{
        competitorId: number;
        platformId: number;
      }>,
    ) => {
      state.competitors = state.competitors.map((item) => {
        const result = removePlatform({
          prev: item.social_usernames,
          platformId: action.payload.platformId,
          orders: item.orders,
        });
        if (item.id === action.payload.competitorId) {
          return {
            ...item,
            mode: item.mode === "existing" ? "edited" : item.mode,
            orders: result.ordersResult,
            social_usernames: result.rest,
          };
        } else {
          return item;
        }
      });
    },
    removeCompetitorUsername: (
      state,
      action: PayloadAction<{
        competitorId: number;
        platformId: number;
        usernameId: number;
      }>,
    ) => {
      state.competitors = state.competitors.map((item) =>
        item.id === action.payload.competitorId
          ? {
              ...item,
              social_usernames: removeUsernameItemSection({
                prev: item.social_usernames,
                platformId: action.payload.platformId,
                usernameId: action.payload.usernameId,
              }).rest,
            }
          : item,
      );
    },
    addCompetitorUsernameSection: (
      state,
      action: PayloadAction<{
        competitorId: number;
        platformId: number;
      }>,
    ) => {
      state.competitors = state.competitors.map((item) =>
        item.id === action.payload.competitorId
          ? {
              ...item,
              social_usernames: addNewUsernameSection({
                prev: item.social_usernames,
                platformId: action.payload.platformId,
              }),
            }
          : item,
      );
    },
    updateCompetitorUsernameSection: (
      state,
      action: PayloadAction<{
        competitorId: number;
        platformId: number;
        data: UsernameItem;
      }>,
    ) => {
      state.competitors = state.competitors.map((item) =>
        item.id === action.payload.competitorId
          ? {
              ...item,
              mode: item.mode === "existing" ? "edited" : item.mode,
              social_usernames: updateUsernameItem({
                prev: item.social_usernames,
                platformId: action.payload.platformId,
                data: action.payload.data,
              }).rest,
            }
          : item,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSocialUsernames.pending, (state) => {
      state.isLoadingSocialUsernames = true;
    });
    builder.addCase(
      fetchSocialUsernames.fulfilled,
      (state, action: PayloadAction<UsernameItemType[]>) => {
        state.isLoadingSocialUsernames = false;
        if (action.payload) {
          state.usernames = getUsernamesByPlatform(action.payload);
          const orderSet = new Set(
            action.payload.map(({ platform }) => platform.id),
          );
          state.usernamesOrder = [...orderSet];
        }
      },
    );

    builder.addCase(fetchCompetitors.pending, (state) => {
      state.isLoadingCompetitors = true;
    });
    builder.addCase(
      fetchCompetitors.fulfilled,
      (state, action: PayloadAction<CompetitorItemType[]>) => {
        state.isLoadingCompetitors = false;
        if (action.payload.length > 0) {
          state.competitors = action.payload.map(
            ({ id, website, strength, social_usernames }) => ({
              mode: "existing",
              id: id,
              website: website,
              strength,
              social_usernames: getUsernamesByPlatform(social_usernames),
              orders: [
                ...new Set(social_usernames.map(({ platform }) => platform.id)),
              ],
            }),
          );
        } else {
          const newCompetitor: CompetitorLocalItemType = getNewCompetitorItem();
          state.competitors = [newCompetitor];
        }
      },
    );
    builder.addCase(fetchCompetitors.rejected, (state) => {
      state.isLoadingCompetitors = false;
    });
    builder.addCase(fetchCreatorPartnerUsernames.pending, (state) => {
      state.isLoadingCreatorPartnerUsernames = true;
    });
    builder.addCase(
      fetchCreatorPartnerUsernames.fulfilled,
      (state, action: PayloadAction<UsernameItemType[]>) => {
        state.isLoadingCreatorPartnerUsernames = false;
        if (action.payload) {
          state.partnerUsernames = getUsernamesByPlatform(action.payload);
          const orderSet = new Set(
            action.payload.map(({ platform }) => platform.id),
          );
          state.partnerUsernamesOrder = [...orderSet];
        }
      },
    );
    builder.addCase(fetchCreatorPartnerUsernames.rejected, (state) => {
      state.isLoadingCreatorPartnerUsernames = false;
    });
    builder.addCase(validateSocialHandle.pending, (state) => {
      state.isLoadingValidate = true;
    });
    builder.addCase(validateSocialHandle.fulfilled, (state, action) => {
      state.isLoadingValidate = false;
      state.validSocialUsernames = [
        ...state.validSocialUsernames,
        action.payload,
      ];
    });
    builder.addCase(validateSocialHandle.rejected, (state) => {
      state.isLoadingValidate = false;
    });
  },
});

export const {
  selectPlatformUsername,
  selectPlatformPartnerUsername,
  removePlatformWithUsernames,
  removePlatformWithPartnerUsernames,
  addUsernameSection,
  addPartnerUsernameSection,
  removeUsernameSection,
  removePartnerUsernameSection,
  updateUsernameSection,
  updatePartnerUsernameSection,
  addCompetitor,
  removeCompetitor,
  updateCompetitor,
  selectCompetitorPlatform,
  removeCompetitorPlatform,
  removeCompetitorUsername,
  addCompetitorUsernameSection,
  updateCompetitorUsernameSection,
} = brandSocialHandlesSlice.actions;
